import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Plus, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatTime } from '@/lib/utils';
import { useAuth } from '@/lib/use-auth';
import {
  ACTION_LABEL,
  STATUS_LABEL as CORRECTION_STATUS_LABEL,
  STATUS_TONE as CORRECTION_STATUS_TONE,
  type CorrectionItem,
  type CorrectionSummary,
} from './corrections/types';
import {
  STATUS_LABEL as ABSENCE_STATUS_LABEL,
  STATUS_TONE as ABSENCE_STATUS_TONE,
  type AbsenceBalance,
  type AbsenceRequestSummary,
} from './absences/types';
import { AbsenceRequestModal } from './absences/AbsenceRequestModal';
import { OvertimeRequestModal } from './overtime/OvertimeRequestModal';
import { OvertimeApprovalPanel } from './overtime/OvertimeApprovalPanel';
import {
  STATUS_LABEL_OVERTIME,
  STATUS_TONE_OVERTIME,
  formatDaysAndHours,
  formatHm,
  type OvertimeBalance,
  type OvertimeRequestSummary,
  type PendingOvertimeWorkday,
} from './overtime/types';

function showMutationError(action: string) {
  return (err: unknown) => {
    const msg = err instanceof ApiError ? err.message : `Error al ${action}`;
    window.alert(msg);
  };
}

type Tab = 'mine' | 'overtime' | 'pending';

export function Requests() {
  const { user } = useAuth();
  const canApprove =
    user?.role === 'MANAGER' || user?.role === 'HR_ADMIN' || user?.role === 'SUPER_ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab =
    tabParam === 'pending' && canApprove
      ? 'pending'
      : tabParam === 'overtime'
        ? 'overtime'
        : 'mine';
  const setTab = (next: Tab) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'mine') sp.delete('tab');
    else sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  // Contador para badge en la pestaña "Horas a compensar" (pendientes de decidir).
  const pendingOvertime = useQuery({
    queryKey: ['overtime', 'workday', 'pending'],
    queryFn: () => api<PendingOvertimeWorkday[]>('/overtime/workday/pending'),
  });
  const pendingOvertimeCount = pendingOvertime.data?.length ?? 0;

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tightish">Solicitudes</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Correcciones de fichaje, ausencias y horas a compensar.
          </p>
        </header>

        <nav className="flex gap-1 border-b border-border px-5">
          <TabButton active={tab === 'mine'} onClick={() => setTab('mine')}>
            Mías
          </TabButton>
          <TabButton active={tab === 'overtime'} onClick={() => setTab('overtime')}>
            Horas a compensar
            {pendingOvertimeCount > 0 ? (
              <Badge tone="amber" dot className="ml-1.5">
                {pendingOvertimeCount}
              </Badge>
            ) : null}
          </TabButton>
          {canApprove ? (
            <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
              Por aprobar
            </TabButton>
          ) : null}
        </nav>

        <div className="p-7">
          {tab === 'mine' ? <MyRequestsTab /> : null}
          {tab === 'overtime' ? <OvertimeApprovalPanel /> : null}
          {tab === 'pending' && canApprove ? <PendingApprovalTab /> : null}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors tracking-tightish',
        active
          ? 'border-icaria-600 text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// =============================================================================
// MIS SOLICITUDES
// =============================================================================

function MyRequestsTab() {
  return (
    <div className="space-y-7">
      <BalancesPanel />
      <MyAbsencesSection />
      <MyCorrectionsSection />
    </div>
  );
}

function BalancesPanel() {
  const balances = useQuery({
    queryKey: ['absences', 'balance'],
    queryFn: () => api<AbsenceBalance[]>('/absences/balance'),
  });
  const overtime = useQuery({
    queryKey: ['overtime', 'balance'],
    queryFn: () => api<OvertimeBalance>('/overtime/balance'),
  });
  const items = (balances.data ?? []).filter((b) => b.consumesBalance);
  if (balances.isLoading && overtime.isLoading) return null;
  return (
    <section>
      <Eyebrow>Saldos disponibles</Eyebrow>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
        {items.map((b) => (
          <div key={b.absenceTypeId} className="rounded-sm border border-border bg-paper px-5 py-4">
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: b.color }}
                aria-hidden
              />
              <p className="text-[12px] text-muted-foreground">{b.name}</p>
            </div>
            <p
              className={cn(
                'font-mono text-[24px] font-medium tabular-nums tracking-tight2 mt-1',
                b.availableDays < 0 && 'text-destructive',
              )}
            >
              {b.availableDays} <span className="text-[14px] text-muted-foreground font-normal">días</span>
            </p>
          </div>
        ))}
        {overtime.data ? <OvertimeBalanceCard balance={overtime.data} /> : null}
      </div>
    </section>
  );
}

function OvertimeBalanceCard({ balance }: { balance: OvertimeBalance }) {
  const [modalOpen, setModalOpen] = useState(false);
  const hasBalance = balance.availableMinutes > 0;
  return (
    <div className="rounded-sm border border-border bg-paper px-5 py-4 flex flex-col justify-between gap-2">
      <div>
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full flex-shrink-0 bg-icaria-600"
            aria-hidden
          />
          <p className="text-[12px] text-muted-foreground">Saldos a compensar</p>
        </div>
        <p className="font-mono text-[24px] font-medium tabular-nums tracking-tight2 mt-1">
          {balance.availableDays}{' '}
          <span className="text-[14px] text-muted-foreground font-normal">
            d{balance.availableRemainderMinutes > 0
              ? ` ${formatHm(balance.availableRemainderMinutes)}`
              : ''}
          </span>
        </p>
        {balance.pendingMinutes > 0 ? (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {formatHm(balance.pendingMinutes)} reservados en solicitudes pendientes.
          </p>
        ) : null}
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="-ml-2 h-auto w-full justify-start whitespace-normal py-1.5 text-left leading-snug"
        onClick={() => setModalOpen(true)}
        disabled={!hasBalance}
      >
        <Plus className="h-3.5 w-3.5 flex-shrink-0" />
        Convertir a días de descanso compensatorio
      </Button>
      <OvertimeRequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

function MyAbsencesSection() {
  const queryClient = useQueryClient();
  const absences = useQuery({
    queryKey: ['absences', 'mine'],
    queryFn: () => api<AbsenceRequestSummary[]>('/absences'),
  });
  const overtimeRequests = useQuery({
    queryKey: ['overtime', 'mine'],
    queryFn: () => api<OvertimeRequestSummary[]>('/overtime'),
  });
  const cancelAbsence = useMutation({
    mutationFn: (id: string) =>
      api<AbsenceRequestSummary>(`/absences/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('cancelar la ausencia'),
  });
  const cancelOvertime = useMutation({
    mutationFn: (id: string) =>
      api<OvertimeRequestSummary>(`/overtime/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('cancelar el canje'),
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const merged = useMergedRequests(absences.data, overtimeRequests.data);
  const loading = absences.isLoading || overtimeRequests.isLoading;
  const error = absences.error ?? overtimeRequests.error;

  return (
    <section>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <CollapseHeader open={open} onToggle={() => setOpen((o) => !o)} count={merged.length}>
          Mis ausencias y canjes
        </CollapseHeader>
        <Button
          size="sm"
          variant="primary"
          onClick={(e) => {
            e.stopPropagation();
            setModalOpen(true);
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Nueva ausencia
        </Button>
      </div>

      {open ? (
        <RequestsListBody
          loading={loading}
          error={error}
          empty="Aún no has solicitado nada."
        >
          {merged.map((item) =>
            item.kind === 'absence' ? (
              <AbsenceCard
                key={`abs-${item.data.id}`}
                absence={item.data}
                right={
                  canCancelAbsence(item.data) ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => cancelAbsence.mutate(item.data.id)}
                      disabled={cancelAbsence.isPending}
                    >
                      Cancelar
                    </Button>
                  ) : null
                }
              />
            ) : (
              <OvertimeCard
                key={`ot-${item.data.id}`}
                request={item.data}
                right={
                  item.data.status === 'PENDING' || item.data.status === 'APPROVED' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => cancelOvertime.mutate(item.data.id)}
                      disabled={cancelOvertime.isPending}
                    >
                      Cancelar
                    </Button>
                  ) : null
                }
              />
            ),
          )}
        </RequestsListBody>
      ) : null}

      <AbsenceRequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}

function MyCorrectionsSection() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['corrections', 'mine'],
    queryFn: () => api<CorrectionSummary[]>('/corrections'),
  });
  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      api<CorrectionSummary>(`/corrections/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      queryClient.invalidateQueries({ queryKey: ['workdays'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('cancelar la corrección'),
  });
  const [open, setOpen] = useState(false);
  const count = list.data?.length ?? 0;

  return (
    <section>
      <CollapseHeader open={open} onToggle={() => setOpen((o) => !o)} count={count}>
        Mis correcciones
      </CollapseHeader>
      {open ? (
        <>
          <p className="text-[12px] text-muted-foreground mb-3 mt-2">
            Solicítalas desde "Mi jornada" haciendo click en un día.
          </p>
          <RequestsListBody
            loading={list.isLoading}
            error={list.error}
            empty="No tienes correcciones pendientes."
          >
            {(list.data ?? []).map((r) => (
              <CorrectionCard
                key={r.id}
                request={r}
                right={
                  r.status === 'PENDING' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => cancelMutation.mutate(r.id)}
                      disabled={cancelMutation.isPending}
                    >
                      Cancelar
                    </Button>
                  ) : null
                }
              />
            ))}
          </RequestsListBody>
        </>
      ) : null}
    </section>
  );
}

// =============================================================================
// POR APROBAR
// =============================================================================

function PendingApprovalTab() {
  return (
    <div className="space-y-7">
      <PendingAbsencesSection />
      <PendingCorrectionsSection />
    </div>
  );
}

function PendingAbsencesSection() {
  const queryClient = useQueryClient();
  const absences = useQuery({
    queryKey: ['absences', 'pending'],
    queryFn: () => api<AbsenceRequestSummary[]>('/absences/pending'),
  });
  const overtimeRequests = useQuery({
    queryKey: ['overtime', 'pending'],
    queryFn: () => api<OvertimeRequestSummary[]>('/overtime/pending'),
  });
  const approveAbsence = useMutation({
    // `isJustified` sólo viaja en las ausencias por horas: es la decisión de
    // quien aprueba sobre si recibió el justificante.
    mutationFn: (args: { id: string; isJustified?: boolean }) =>
      api<AbsenceRequestSummary>(`/absences/${args.id}/approve`, {
        method: 'POST',
        body: JSON.stringify(
          args.isJustified === undefined ? {} : { isJustified: args.isJustified },
        ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('aprobar la ausencia'),
  });
  const rejectAbsence = useMutation({
    mutationFn: (args: { id: string; notes: string }) =>
      api<AbsenceRequestSummary>(`/absences/${args.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ decisionNotes: args.notes || undefined }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('rechazar la ausencia'),
  });
  const approveOvertime = useMutation({
    mutationFn: (id: string) =>
      api<OvertimeRequestSummary>(`/overtime/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('aprobar el canje'),
  });
  const rejectOvertime = useMutation({
    mutationFn: (args: { id: string; notes: string }) =>
      api<OvertimeRequestSummary>(`/overtime/${args.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ decisionNotes: args.notes || undefined }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtime'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('rechazar el canje'),
  });

  const merged = useMergedRequests(absences.data, overtimeRequests.data);
  const loading = absences.isLoading || overtimeRequests.isLoading;
  const error = absences.error ?? overtimeRequests.error;

  return (
    <section>
      <Eyebrow>Ausencias y canjes pendientes</Eyebrow>
      <div className="mt-3">
        <RequestsListBody
          loading={loading}
          error={error}
          empty="No hay solicitudes pendientes."
        >
          {merged.map((item) =>
            item.kind === 'absence' ? (
              <AbsenceCard
                key={`abs-${item.data.id}`}
                absence={item.data}
                showRequester
                right={
                  item.data.status === 'PENDING' ? (
                    <ApproveRejectButtons
                      onApprove={() => {
                        const a = item.data;
                        if (!a.startTime) {
                          approveAbsence.mutate({ id: a.id });
                          return;
                        }
                        // Horas justificadas: quien aprueba confirma si tiene
                        // el justificante en la mano. De eso depende que se
                        // descuenten o no de las vacaciones del empleado.
                        const justified = window.confirm(
                          `${a.user?.fullName ?? 'El empleado'} pide ${a.startTime}–${a.endTime} ` +
                            `y declara que ${a.isJustified ? 'SÍ' : 'NO'} entrega justificante.\n\n` +
                            'Aceptar = justificada (no gasta vacaciones)\n' +
                            'Cancelar = sin justificante (se descuenta de vacaciones)',
                        );
                        approveAbsence.mutate({ id: a.id, isJustified: justified });
                      }}
                      onReject={() => {
                        const notes = window.prompt('Motivo del rechazo (opcional)') ?? '';
                        rejectAbsence.mutate({ id: item.data.id, notes });
                      }}
                      disabled={approveAbsence.isPending || rejectAbsence.isPending}
                    />
                  ) : null
                }
              />
            ) : (
              <OvertimeCard
                key={`ot-${item.data.id}`}
                request={item.data}
                showRequester
                right={
                  item.data.status === 'PENDING' ? (
                    <ApproveRejectButtons
                      onApprove={() => approveOvertime.mutate(item.data.id)}
                      onReject={() => {
                        const notes = window.prompt('Motivo del rechazo (opcional)') ?? '';
                        rejectOvertime.mutate({ id: item.data.id, notes });
                      }}
                      disabled={approveOvertime.isPending || rejectOvertime.isPending}
                    />
                  ) : null
                }
              />
            ),
          )}
        </RequestsListBody>
      </div>
    </section>
  );
}

function PendingCorrectionsSection() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['corrections', 'pending'],
    queryFn: () => api<CorrectionSummary[]>('/corrections/pending'),
  });
  const approveMutation = useMutation({
    mutationFn: (id: string) =>
      api<CorrectionSummary>(`/corrections/${id}/approve`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      queryClient.invalidateQueries({ queryKey: ['workdays'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('aprobar la corrección'),
  });
  const rejectMutation = useMutation({
    mutationFn: (args: { id: string; notes: string }) =>
      api<CorrectionSummary>(`/corrections/${args.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ decisionNotes: args.notes || undefined }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corrections'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: showMutationError('rechazar la corrección'),
  });

  return (
    <section>
      <Eyebrow>Correcciones pendientes</Eyebrow>
      <div className="mt-3">
        <RequestsListBody
          loading={list.isLoading}
          error={list.error}
          empty="No hay correcciones pendientes."
        >
          {(list.data ?? []).map((r) => (
            <CorrectionCard
              key={r.id}
              request={r}
              showRequester
              right={
                r.status === 'PENDING' ? (
                  <ApproveRejectButtons
                    onApprove={() => approveMutation.mutate(r.id)}
                    onReject={() => {
                      const notes = window.prompt('Motivo del rechazo (opcional)') ?? '';
                      rejectMutation.mutate({ id: r.id, notes });
                    }}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  />
                ) : null
              }
            />
          ))}
        </RequestsListBody>
      </div>
    </section>
  );
}

// =============================================================================
// PRESENTATIONAL HELPERS
// =============================================================================

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
      style={{ letterSpacing: '0.14em' }}
    >
      {children}
    </p>
  );
}

function CollapseHeader({
  open,
  onToggle,
  count,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] font-medium uppercase text-muted-foreground hover:text-foreground transition-colors"
      style={{ letterSpacing: '0.14em' }}
    >
      {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      <span>{children}</span>
      <span className="font-sans text-[11px] font-normal normal-case tracking-normal text-muted-foreground/80">
        ({count})
      </span>
    </button>
  );
}

function ApproveRejectButtons({
  onApprove,
  onReject,
  disabled,
}: {
  onApprove: () => void;
  onReject: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="primary" onClick={onApprove} disabled={disabled}>
        <Check className="h-3.5 w-3.5" />
        Aprobar
      </Button>
      <Button size="sm" variant="destructive" onClick={onReject} disabled={disabled}>
        <X className="h-3.5 w-3.5" />
        Rechazar
      </Button>
    </div>
  );
}

function RequestsListBody({
  loading,
  error,
  empty,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty: string;
  children: React.ReactNode;
}) {
  if (loading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error instanceof ApiError ? error.message : 'Error cargando datos'}
      </p>
    );
  }
  const arr = Array.isArray(children) ? children : [children];
  if (arr.filter(Boolean).length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>;
  }
  return <ul className="space-y-3">{children}</ul>;
}

function AbsenceCard({
  absence,
  right,
  showRequester,
}: {
  absence: AbsenceRequestSummary;
  right?: React.ReactNode;
  showRequester?: boolean;
}) {
  return (
    <li className="rounded-sm border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: absence.absenceType.color }}
              aria-hidden
            />
            <span className="font-semibold text-[14px] text-foreground">
              {showRequester
                ? `${absence.user?.fullName ?? '—'} · ${absence.absenceType.name}`
                : absence.absenceType.name}
            </span>
            <Badge tone={ABSENCE_STATUS_TONE[absence.status]} dot>
              {ABSENCE_STATUS_LABEL[absence.status]}
            </Badge>
          </div>
          <p className="font-mono text-[12px] text-muted-foreground mt-1.5">
            {absence.startDate.slice(0, 10)}
            {absence.startDate.slice(0, 10) !== absence.endDate.slice(0, 10)
              ? ` → ${absence.endDate.slice(0, 10)}`
              : null}
            {absence.isHalfDay
              ? ` · medio día (${absence.halfDayPart === 'AM' ? 'mañana' : 'tarde'})`
              : null}
            {absence.startTime && absence.endTime
              ? ` · ${absence.startTime}–${absence.endTime}`
              : null}
          </p>
          {absence.startTime ? (
            <p className="text-[12px] mt-1">
              {absence.isJustified ? (
                <span className="text-success">
                  Justificada · no descuenta vacaciones
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Sin justificante · se descuenta de vacaciones
                </span>
              )}
            </p>
          ) : null}
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>
      {absence.comment ? (
        <p className="text-[13px] text-ink-2 whitespace-pre-wrap mt-3 leading-relaxed">
          {absence.comment}
        </p>
      ) : null}
      {absence.decisionNotes ? (
        <p className="text-[12px] text-muted-foreground mt-2">
          <span className="font-medium text-ink-2">Notas:</span> {absence.decisionNotes}
        </p>
      ) : null}
    </li>
  );
}

function CorrectionCard({
  request,
  right,
  showRequester,
}: {
  request: CorrectionSummary;
  right?: React.ReactNode;
  showRequester?: boolean;
}) {
  return (
    <li className="rounded-sm border border-border bg-card p-5 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-semibold text-[14px] text-foreground">
              {showRequester ? request.requester?.fullName ?? '—' : 'Corrección'}
            </span>
            <Badge tone={CORRECTION_STATUS_TONE[request.status]} dot>
              {CORRECTION_STATUS_LABEL[request.status]}
            </Badge>
          </div>
          <p className="font-mono text-[12px] text-muted-foreground mt-1.5">
            Fecha objetivo: {request.targetDate.slice(0, 10)} · Items: {request.items.length}
          </p>
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>

      <p className="text-[13px] text-ink-2 whitespace-pre-wrap leading-relaxed">{request.reason}</p>

      <ul className="space-y-1 text-[12px]">
        {request.items.map((it) => (
          <ItemSummary key={it.id} item={it} />
        ))}
      </ul>

      {request.decisionNotes ? (
        <p className="text-[12px] text-muted-foreground">
          <span className="font-medium text-ink-2">Notas:</span> {request.decisionNotes}
        </p>
      ) : null}
    </li>
  );
}

function ItemSummary({ item }: { item: CorrectionItem }) {
  const time = item.proposedEventAt ? formatTime(item.proposedEventAt) : null;
  return (
    <li className="flex items-center gap-2 text-muted-foreground">
      <span className="font-medium text-foreground min-w-[64px]">{ACTION_LABEL[item.action]}</span>
      {item.action === 'ADD' ? (
        <span>
          {item.proposedType === 'IN' ? 'Entrada' : 'Salida'} a las{' '}
          <span className="font-mono">{time}</span>
        </span>
      ) : null}
      {item.action === 'MODIFY' ? (
        <span>
          {item.proposedType ? `tipo ${item.proposedType}` : 'cambio de hora'}
          {time ? <> a las <span className="font-mono">{time}</span></> : null}
        </span>
      ) : null}
      {item.action === 'VOID' ? <span>punch existente</span> : null}
    </li>
  );
}

// =============================================================================
// OVERTIME (canjes de horas a compensar → vacaciones)
// =============================================================================

type MergedRequestItem =
  | { kind: 'absence'; sortKey: number; data: AbsenceRequestSummary }
  | { kind: 'overtime'; sortKey: number; data: OvertimeRequestSummary };

function useMergedRequests(
  absences: AbsenceRequestSummary[] | undefined,
  overtime: OvertimeRequestSummary[] | undefined,
): MergedRequestItem[] {
  const a: MergedRequestItem[] = (absences ?? []).map((r) => ({
    kind: 'absence',
    sortKey: new Date(r.createdAt).getTime(),
    data: r,
  }));
  const o: MergedRequestItem[] = (overtime ?? []).map((r) => ({
    kind: 'overtime',
    sortKey: new Date(r.createdAt).getTime(),
    data: r,
  }));
  return [...a, ...o].sort((x, y) => {
    // Pendientes primero, luego por fecha desc.
    const xPending = x.data.status === 'PENDING' ? 0 : 1;
    const yPending = y.data.status === 'PENDING' ? 0 : 1;
    if (xPending !== yPending) return xPending - yPending;
    return y.sortKey - x.sortKey;
  });
}

function OvertimeCard({
  request,
  right,
  showRequester,
}: {
  request: OvertimeRequestSummary;
  right?: React.ReactNode;
  showRequester?: boolean;
}) {
  const days = Number(request.daysGrantedSnapshot);
  return (
    <li className="rounded-sm border border-border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span
              className="h-2.5 w-2.5 rounded-full flex-shrink-0 bg-icaria-600"
              aria-hidden
            />
            <span className="font-semibold text-[14px] text-foreground">
              {showRequester
                ? `${request.user?.fullName ?? '—'} · Canje a compensar → días de descanso compensatorio`
                : 'Canje a compensar → días de descanso compensatorio'}
            </span>
            <Badge tone="blue" dot>
              Conversión
            </Badge>
            <Badge tone={STATUS_TONE_OVERTIME[request.status]} dot>
              {STATUS_LABEL_OVERTIME[request.status]}
            </Badge>
          </div>
          <p className="font-mono text-[12px] text-muted-foreground mt-1.5">
            {formatDaysAndHours(request.minutesRequested, request.dayMinutesAtRequest)} →{' '}
            {days} día{days === 1 ? '' : 's'} de descanso compensatorio
            {request.inputMode === 'HOURS' ? ' · pedido en horas' : ' · pedido en días'}
          </p>
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>
      {request.comment ? (
        <p className="text-[13px] text-ink-2 whitespace-pre-wrap mt-3 leading-relaxed">
          {request.comment}
        </p>
      ) : null}
      {request.decisionNotes ? (
        <p className="text-[12px] text-muted-foreground mt-2">
          <span className="font-medium text-ink-2">Notas:</span> {request.decisionNotes}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Una ausencia sólo se puede cancelar si aún no ha empezado: cancelar devuelve
 * TODOS sus días al saldo, así que hacerlo sobre una ya disfrutada regalaría
 * esos días. Las PENDING sí se cancelan siempre (no han consumido saldo).
 */
function canCancelAbsence(a: AbsenceRequestSummary): boolean {
  if (a.status === 'PENDING') return true;
  if (a.status !== 'APPROVED') return false;
  return a.startDate.slice(0, 10) > todayYmd();
}

function todayYmd(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
